/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import React, {useRef} from 'react';

import {useTranslation} from 'react-i18next';

import {AwesomeIcon, ElementPosition, ErrorType} from '@dvrd/dvr-controls';

import Attachment from '../../../../models/attachmentModel';

import {AppContext} from '../../../misc/appContext';

import {BolmateButton} from '../../../controls';

import {showDialog, showSnackbar} from '@dvrd/dvr-controls';

interface Props {
    onRefresh: (attachments: Attachment[]) => void;
    onUpload: VoidFunction;
}

export default function AttachmentUpload(props: Props) {
    const {t} = useTranslation();
    const context = React.useContext(AppContext),
        ref = useRef<HTMLInputElement>(null);

    function onUpload(files: FileList | null) {
        const {account} = context.accountContext;
        if (!account) return;
        if (!files || files.length === 0) return;
        const isMultiple = files.length > 1;
        props.onUpload();
        Attachment.upload(files, account.id, (attachments: Attachment[], error: ErrorType) => {
            props.onRefresh(attachments);
            if (error) showDialog(error, `${t('email.attachment.upload.failed', 'Upload (deels) mislukt')}: ${error}`);
            else {
                const msg = isMultiple
                    ? t('email.attachment.upload.manyUploaded', 'De bijlages zijn geüploaded')
                    : t('email.attachment.upload.oneUploaded', 'De bijlage is geüploaded');
                showSnackbar(msg);
            }
        });
    }

    function onClick() {
        ref.current?.click();
    }

    return (
        <div>
            <BolmateButton
                onClick={onClick}
                label={t('email.attachment.upload.upload', 'Uploaden')}
                ornaments={{
                    element: <AwesomeIcon name='upload' />,
                    placement: ElementPosition.RIGHT
                }}
                secondary
            />
            <input
                type='file'
                style={{display: 'none'}}
                ref={ref}
                onChange={event => onUpload(event.currentTarget.files)}
                multiple
            />
        </div>
    );
}
